import React from 'react';
import { AlertTriangle, Check, RefreshCw, Sparkles, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { AiScoreGauge } from './AiScoreGauge';
import { usePlatform } from '../../contexts/PlatformContext';
import { useAiRun } from '../../hooks/useAiRun';
import { weightedScore, type Bands } from '../../utils/scoring';
import type { ApplicationRecord } from '../../types/domain';
const RUN_STAGES = ['Reading submitted documents', 'Matching preset criteria set', 'Scoring against weighted criteria', 'Detecting risk flags and gaps', 'Ranking preset recommendations'];
interface AiInsightRailProps {
  record: ApplicationRecord;
  bands?: Bands;
  criteriaSetLabel: string;
}
export function AiInsightRail({
  record,
  bands,
  criteriaSetLabel
}: AiInsightRailProps) {
  const {
    setRecommendationStatus
  } = usePlatform();
  const run = useAiRun({
    stages: RUN_STAGES,
    stageDuration: 700
  });
  const score = weightedScore(record.criteria);
  const running = run.state === 'running';
  return <aside className="flex flex-col gap-4 rounded-sm bg-uae-green p-4 text-white" data-id="element-47" data-mp="dp4a8b5zr7gn">
      <div className="flex items-start justify-between gap-3" data-id="element-48" data-mp="8nevoljk8bnt">
        <div data-id="element-49" data-mp="nlh2d4lw1d9a">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/60" data-id="element-50" data-mp="nk5zr846ibjr">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" data-id="element-51" data-mp="imnqfgm91ub8" />
            AI assessment
          </p>
          <p className="mt-1 text-[11px] text-white/55" data-id="element-52" data-mp="3r27bhscro6f7">{criteriaSetLabel}</p>
        </div>
        <Button variant="ghost" size="xs" onClick={run.start} disabled={running} className="text-white/75 hover:bg-white/10 hover:text-white" data-id="element-53" data-mp="3m4e97ael3wk7">
          <RefreshCw className={`mr-1 h-3 w-3 ${running ? 'animate-spin' : ''}`} aria-hidden="true" data-id="element-54" data-mp="3vzpnxspzt3ry" />
          {running ? 'Scoring' : 'Re-run'}
        </Button>
      </div>

      {running ? <div className="space-y-3" aria-live="polite" data-id="element-55" data-mp="3t0h2mh1bb4gc">
          <div className="flex items-center gap-4" data-id="element-56" data-mp="3szpiq3h2uv5w">
            <Skeleton className="h-[104px] w-[104px] rounded-full bg-white/10" data-id="element-57" data-mp="wg8ztdx6ywx" />
            <div className="flex-1 space-y-2" data-id="element-58" data-mp="ubu6gehgfhv">
              <Skeleton className="h-3 w-24 bg-white/10" data-id="element-59" data-mp="5og1x1vkork0" />
              <Skeleton className="h-3 w-32 bg-white/10" data-id="element-60" data-mp="amhwok2d2fbb" />
            </div>
          </div>
          <div className="rounded-sm bg-white/5 p-3" data-id="element-61" data-mp="aoc5x149sizk">
            <p className="text-[11px] text-white/70" data-id="element-62" data-mp="3a2y2y40quy6l">{run.currentStage}</p>
            <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/10" data-id="element-63" data-mp="3e2opfvj8ms5b">
              <div className="h-full bg-white/60 transition-all duration-500" style={{
            width: `${run.progress}%`
          }} data-id="element-64" data-mp="3j2krhp8dk9i8" />
            </div>
          </div>
        </div> : <AiScoreGauge score={score} bands={bands} caption={`Scored across ${record.criteria.length} weighted criteria. Every criterion is evidence-linked and can be overridden by the assigned officer.`} data-id="element-65" data-mp="ycvelhr8exs6" />}

      <div data-id="element-66" data-mp="2nvajbeshs8qy">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/55" data-id="element-67" data-mp="2iu4lx3nvzxb3">
          Criterion breakdown
        </p>
        <ul className="mt-2 space-y-2.5" data-id="element-68" data-mp="2e2mkkehybupl">
          {record.criteria.map(criterion => <li key={criterion.id} data-id="element-69" data-mp="29537hhwzpnz8">
              <div className="flex items-baseline justify-between gap-3" data-id="element-70" data-mp="293s1rcjzi65h">
                <span className="text-[12px] text-white/90" data-id="element-71" data-mp="253rcsxz9eck6">{criterion.label}</span>
                <span className="shrink-0 font-mono text-[11px] text-white/70" data-id="element-72" data-mp="203dh8feugv31">
                  {criterion.score} · w{criterion.weight}
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/10" data-id="element-73" data-mp="1q5p8umobfp2e">
                <div className="h-full rounded-full bg-white/55" style={{
              width: `${criterion.score}%`
            }} data-id="element-74" data-mp="3pikyavo2r4x5" />
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-white/50" data-id="element-75" data-mp="3khdq4vi0s3py">
                {criterion.evidence}
              </p>
            </li>)}
        </ul>
      </div>

      {record.flags.length > 0 && <div data-id="element-76" data-mp="3im09c2j8330h">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/55" data-id="element-77" data-mp="3imi22p6oez3n">
            Risk flags
          </p>
          <ul className="mt-2 space-y-2" data-id="element-78" data-mp="3njrywpwead1i">
            {record.flags.map(flag => <li key={flag.id} className="rounded-sm bg-white/5 p-2.5" data-id="element-79" data-mp="39sn7uc73et2s">
                <p className="flex items-start gap-1.5 text-[12px] font-medium text-white" data-id="element-80" data-mp="3ek508bv1vlc5">
                  <AlertTriangle className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${flag.severity === 'critical' ? 'text-[#ff9d9b]' : 'text-[#f0c94f]'}`} aria-hidden="true" data-id="element-81" data-mp="2zvqwzztq1uv2" />
                  {flag.label}
                </p>
                <p className="mt-1 pl-5 text-[11px] leading-relaxed text-white/60" data-id="element-82" data-mp="2uwnqsc209ili">
                  {flag.detail}
                </p>
              </li>)}
          </ul>
        </div>}

      <div data-id="element-83" data-mp="279sl79edx7pz">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/55" data-id="element-84" data-mp="27a282o44uyrg">
          Recommendations
        </p>
        {record.recommendations.length === 0 ? <p className="mt-2 rounded-sm bg-white/5 p-2.5 text-[11px] text-white/60" data-id="element-85" data-mp="2g9767tbs8q33">
            No preset rule triggered for this record.
          </p> : <ul className="mt-2 space-y-2" data-id="element-86" data-mp="2v4mwdn98s1u7">
            {record.recommendations.map(recommendation => <li key={recommendation.id} className="rounded-sm bg-white/5 p-2.5" data-id="element-87" data-mp="33z9wc3w4zlz2">
                <div className="flex items-start justify-between gap-2" data-id="element-88" data-mp="34148aspfk3r0">
                  <p className="text-[12px] font-medium leading-snug text-white" data-id="element-89" data-mp="2zy7vjimkn1uz">
                    {recommendation.title}
                  </p>
                  <span className="shrink-0 font-mono text-[10px] text-white/50" data-id="element-90" data-mp="38ymisnj3ir14">
                    {recommendation.confidence}%
                  </span>
                </div>
                <p className="mt-1 text-[11px] leading-relaxed text-white/60" data-id="element-91" data-mp="26j56spo3rf7a">
                  {recommendation.detail}
                </p>
                <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wide text-white/40" data-id="element-92" data-mp="2flefrb3q30ik">
                  rule {recommendation.ruleId}
                </p>
                {recommendation.status === 'pending' ? <div className="mt-2 flex gap-1.5" data-id="element-93" data-mp="2paobr21ot3by">
                    <Button size="xs" variant="secondary" className="bg-white/90 text-uae-green hover:bg-white" onClick={() => setRecommendationStatus(record.id, recommendation.id, 'accepted')} data-id="element-94" data-mp="2uaao799oyfjp">
                      <Check className="mr-1 h-3 w-3" aria-hidden="true" data-id="element-95" data-mp="13zo8hnxdmp1a" />
                      Accept
                    </Button>
                    <Button size="xs" variant="ghost" className="text-white/70 hover:bg-white/10 hover:text-white" onClick={() => setRecommendationStatus(record.id, recommendation.id, 'overridden')} data-id="element-96" data-mp="11dot7nn7fq6e">
                      <X className="mr-1 h-3 w-3" aria-hidden="true" data-id="element-97" data-mp="mq3ue5xd4d4a" />
                      Override
                    </Button>
                  </div> : <p className="mt-2 text-[11px] text-white/70" data-id="element-98" data-mp="1fd6omlv0a1oq">
                    {recommendation.status === 'accepted' ? 'Accepted by officer — recorded on the timeline' : 'Overridden by officer — recorded on the timeline'}
                  </p>}
              </li>)}
          </ul>}
      </div>
    </aside>;
}